import {ModelingUtil} from "@ispm/shared/bpmn-io/modeling-util";
import {Shape, GuideBox} from 'diagram-js/lib/model';
import { HEAD_NODE_ID } from '@ispm-shared/modules/bpmn';
import {NodeDependencies} from "@ispm/shared/bpmn/models/node-dependencies";

class Path {
    nodes: Array<Node> = [];
    lastMandatoryNote: Node;
    enablingSet: Set<Node> = new Set<Node>();
    lastAncestor: Node;
    copy(): Path {
        let path: Path = new Path();
        path.nodes = [...this.nodes];
        path.lastMandatoryNote = this.lastMandatoryNote;
        path.enablingSet = new Set(this.enablingSet);
        path.lastAncestor = this.lastAncestor;
        return path;
    }
    getEnablingSet(): Set<Node> {
        if (this.enablingSet && this.enablingSet.size > 0) {
            return new Set(this.enablingSet);
        }
        if (this.lastMandatoryNote) {
            return new Set([this.lastMandatoryNote]);
        }
        return new Set();
    }
    id(): string {
        return this.nodes.map(node => node.id).join("->");
    }
}

class PathList {
    paths: Array<Path> = [];
    enablingSet: Set<Node> = new Set();
    computeEnablingSet() {
        this.enablingSet.clear();
        this.paths.forEach(path => {
            let pathEnablingSet: Set<Node> = path.getEnablingSet();
            pathEnablingSet.forEach(node => this.enablingSet.add(node));
        });
    }
}

class Node {
    parents: Node[] = [];
    children: Node[] = [];
    mandatoryNote: boolean = false;
    visited: boolean = false;
    constructor(public id: string) {}
}

export class NodeSelectionDependencies {
    public static computeSelectionDependencyMap(elementRegistry): NodeDependencies {
      let dependencyMap: Map<string, Set<string>> = new Map<string, Set<string>>();
      let replacements: Map<string, Set<string>> = new Map<string, Set<string>>();
      const rootElement: Shape = elementRegistry.get(HEAD_NODE_ID);
      if (rootElement) {
        const rootNode: Node = NodeSelectionDependencies.createNodeTree(elementRegistry, rootElement);
        let commonChildren: Set<Node> = new Set<Node>();
        let nodePathsMap: Map<string, PathList> = new Map<string, PathList>();
        NodeSelectionDependencies.traverseTree(rootNode, new Path(), nodePathsMap, commonChildren);
        NodeSelectionDependencies.handleCommonChildren(commonChildren, nodePathsMap, replacements);
        nodePathsMap.forEach((paths, id) => dependencyMap.set(id, NodeSelectionDependencies.getDependencies(paths)));
      }
      return { dependencies: dependencyMap, replacements };
    }

    private static traverseTree(node: Node, path: Path, nodePathsMap: Map<string, PathList>, commonChildren: Set<Node>) {
        const id: string = node.id;
        let currentNodePaths: PathList = nodePathsMap.get(id);
        if (!currentNodePaths) { // First path to this node
            currentNodePaths = new PathList();
            nodePathsMap.set(id, currentNodePaths);
        } else { // Node already reached by other path
            if (node.parents.length > 1) {
                commonChildren.add(node);
            }
        }
        currentNodePaths.paths.push(path.copy());
        path.nodes.push(node);
        let prevLastMandatoryNote: Node = path.lastMandatoryNote;
        if (node.mandatoryNote) {
            path.lastMandatoryNote = node;
        }
        currentNodePaths.computeEnablingSet();
        node.children.forEach(child => this.traverseTree(child, path, nodePathsMap, commonChildren));
        path.lastMandatoryNote = prevLastMandatoryNote;
        path.nodes.pop();
    }

    private static handleCommonChildren(nodes: Set<Node>, nodePathsMap: Map<string, PathList>, replacements: Map<string, Set<string>>) {
        nodes.forEach(node => NodeSelectionDependencies.handleCommonChild(node, nodePathsMap, replacements));
    }

    private static handleCommonChild(node: Node, nodePathsMap: Map<string, PathList>, replacements: Map<string, Set<string>>) {
        let nodePaths: PathList = nodePathsMap.get(node.id);
        if ((! nodePaths) || (! nodePaths.paths) || (nodePaths.paths.length == 0)) {
            console.log("Error: handleCommonChild - no node paths to " + node.id);
            return;
        }
        let ancestorsPerPath: Map<Path, Set<Node>> = new Map<Path, Set<Node>>();
        for (let i = 0; i < nodePaths.paths.length; i++) {
            for (let j = i + 1; j < nodePaths.paths.length; j++) {
                let p1: Path = nodePaths.paths[i];
                let p2: Path = nodePaths.paths[j];
                let ancestor: Node = NodeSelectionDependencies.findClosestCommonAncestor(p1, p2);
                if (! ancestor) {
                    console.log("Error: handleCommonChild - Could not find nearest ancestor for " + p1.id() + ", " + p2.id());
                    return;
                }
                NodeSelectionDependencies.getAncestors(ancestorsPerPath, p1).add(ancestor);
                NodeSelectionDependencies.getAncestors(ancestorsPerPath, p2).add(ancestor);
            }
        }
        ancestorsPerPath.forEach((nodes, path) => {
            let lastAncestor: Node = NodeSelectionDependencies.findLastOnPath(path, nodes);
            let enablingSetForPath: Set<Node> = NodeSelectionDependencies.getEnablingSet(lastAncestor, path, replacements);
            path.lastAncestor = lastAncestor;
            path.enablingSet = enablingSetForPath;
        });
        nodePaths.computeEnablingSet();
        if (! node.mandatoryNote) {
            node.children.forEach(child => NodeSelectionDependencies.propagateEnablingSetFromCommonChild(child, nodePaths.enablingSet, nodePathsMap));
        }
    }

    private static findClosestCommonAncestor(p1: Path, p2: Path): Node {
        // Get rid of common suffix - in case of 2 paths to the node where the end of the path is the same,
        // such as 1->3->5->7->8 and 1->4->6->7->8.
        let i, j;
        for (i = p1.nodes.length - 1, j = p2.nodes.length - 1; i >= 0 && j >= 0; i--, j--) {
            if (p1.nodes[i] != p2.nodes[j]) {
                break;
            }
        }
        // Now go backwards from the last different node until we find the first same node.
        for (; i >= 0; i--) {
            for (let k = j; k >= 0; k--) {
                if (p1.nodes[i] == p2.nodes[k])
                    return p1.nodes[i];
            }
        }
        console.log("findClosestCommonAncestor: Could not find closest ancestor for paths: " + p1.id() + " and " + p2.id());
        return null;
    }

    private static getAncestors(ancestorsPerPath: Map<Path, Set<Node>>, p: Path): Set<Node> {
        let pathAncestors: Set<Node> = ancestorsPerPath.get(p);
        if (! pathAncestors) {
            pathAncestors = new Set();
            ancestorsPerPath.set(p, pathAncestors);
        }
        return pathAncestors;
    }

    private static findLastOnPath(path: Path, nodes: Set<Node>): Node {
        let lastIndex = -1;
        let lastNode: Node = null;
        nodes.forEach(node => {
            let index = path.nodes.indexOf(node);
            if ((! lastNode) || (index > lastIndex)) {
                lastIndex = index;
                lastNode = node;
            }
        });
        return lastNode;
    }

    private static getEnablingSet(ancestor: Node, path: Path, replacements: Map<string, Set<string>>): Set<Node> {
        let ancestorIndex = path.nodes.indexOf(ancestor) + 1;
        if (ancestorIndex >= path.nodes.length) {
            console.log("Ancestor " + ancestor.id + " is the last on path " + path.id());
            return null;
        }
        let mandatoryNote: Node = null;
        for (let i = ancestorIndex; i < path.nodes.length; i++) {
            let node: Node = path.nodes[i];
            if (node.mandatoryNote) {
                mandatoryNote = node;
            }
        }
        const subPath: Array<Node> = path.nodes.slice(ancestorIndex, path.nodes.length);
        if (mandatoryNote != null) {
          if (! replacements.has(mandatoryNote.id)) {
            replacements.set(mandatoryNote.id, new Set(subPath.map(node => node.id)));
          }
          return new Set([mandatoryNote]);
        } else {
            return new Set(subPath);
        }
    }

    private static propagateEnablingSetFromCommonChild(node: Node, enablingSet: Set<Node>, nodePathsMap: Map<string, PathList>) {
        if (node.parents.length > 1)
            return;
        let paths: PathList = nodePathsMap.get(node.id);
        paths.enablingSet = enablingSet;
        if (node.mandatoryNote)
            return;
        node.children.forEach(child => NodeSelectionDependencies.propagateEnablingSetFromCommonChild(child, enablingSet, nodePathsMap));
    }

    private static getDependencies(paths: PathList) {
        let deps: Set<string> = new Set<string>();
        paths.enablingSet.forEach(node => deps.add(node.id));
        return deps;
    }

    private static createNodeTree(elementRegistry, rootElement: Shape): Node {
        let nodeNameMap: Map<string, Node> = new Map<string, Node>();
        return this.createNodeTreeRecursive(elementRegistry, rootElement, nodeNameMap);
    }

    private static createNodeTreeRecursive(elementRegistry, shape: Shape, nodeNameMap: Map<string, Node>): Node {
        const name: string = shape.id;
        let node: Node = nodeNameMap.get(name);
        if (! node) {
            node = new Node(name);
            nodeNameMap.set(name, node);
        }
        if (node.visited) {
          return node;
        }
        node.visited = true;
        let guideBox: GuideBox = ModelingUtil.getGuideBox(shape);
        if (guideBox && guideBox.automatically && guideBox.automatically !== "false") {
            node.mandatoryNote = true;
        }
        let childNodes = ModelingUtil.getSelectableChildNodes(shape);
        if (childNodes) {
            childNodes.forEach(child => {
                this.createChild(node, child.id, nodeNameMap);
                this.createNodeTreeRecursive(elementRegistry, child, nodeNameMap);
            });
        }
        return node;
    }

    private static createChild(node: Node, childId: string, nodeNameMap: Map<string, Node>) {
        let childNode: Node = nodeNameMap.get(childId);
        if (! childNode) {
            childNode = new Node(childId);
            nodeNameMap.set(childId, childNode);
        }
        node.children.push(childNode);
        childNode.parents.push(node);
    }
}
